import axiosInstance from "utils/axios";
import { toast } from "sonner";

const API_ENDPOINTS = {
    NOTIFICATIONS: "/api/Notification",
};
export const getUserNotifications = async (options = {}) => {
    try {
        const requestBody = {
            pageNumber: options.pageNumber || 1,
            pageSize: options.pageSize || 5,
            // Pass null = all types; "1" = System only; "2" = WhatsApp only; "1,2" = both
            notificationType: options.notificationType ?? null,
            // Pass null = all; "me" = directly responsible; "team" = oversight/FYI only
            scope: options.scope ?? null,
        };

        const response = await axiosInstance.post(
            `${API_ENDPOINTS.NOTIFICATIONS}/GetUserNotifications`,
            requestBody,
            {
                showLoader: false,
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        const responseData = response.data;

        if (responseData.statusCode === 200 && responseData.status === "Success") {
            let notificationsData = [];

            if (typeof responseData.data === "string") {
                try {
                    notificationsData = JSON.parse(responseData.data);
                } catch (parseError) {
                    console.error("Error parsing notifications data:", parseError);
                }
            } else if (Array.isArray(responseData.data)) {
                notificationsData = responseData.data;
            }

            return {
                success: true,
                data: notificationsData,
                message: responseData.message || "Notifications fetched successfully",
                totalRecords: responseData.totalRecords || notificationsData.length,
            };
        } else {
            throw new Error(responseData.message || "Failed to fetch notifications");
        }
    } catch (error) {
        console.error("Error fetching user notifications:", error);

        let errorMessage = "Failed to fetch notifications";

        if (error.response) {
            const status = error.response.status;
            const serverMessage =
                error.response.data?.message || error.response.data?.error;

            if (status >= 400 && status < 500) {
                errorMessage = serverMessage || `Client error: ${status}`;
            } else if (status >= 500) {
                errorMessage = serverMessage || `Server error: ${status}`;
            }
        } else if (error.request) {
            errorMessage = "Network error: Unable to connect to server";
        } else {
            errorMessage = error.message || "Unknown error occurred";
        }

        return {
            success: false,
            error: errorMessage,
            data: [],
            totalRecords: 0,
        };
    }
};


export const getUserNotificationCount = async () => {
    try {
        // Make GET request to the count endpoint
        const response = await axiosInstance.get(
            `${API_ENDPOINTS.NOTIFICATIONS}/GetUserNotificationCount`,
            {
                showLoader: false,
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        const responseData = response.data;

        // Check if request succeeded
        if (responseData.statusCode === 200 && responseData.status === "Success") {
            let notificationData = {};

            // Try parsing if data is a stringified JSON
            if (typeof responseData.data === "string") {
                try {
                    notificationData = JSON.parse(responseData.data);
                } catch (parseError) {
                    console.error("Error parsing notification count data:", parseError);
                }
            } else {
                notificationData = responseData.data || {};
            }

            const unreadCount = notificationData?.UnreadNotificationCount || 0;

            return {
                success: true,
                count: unreadCount,
                message:
                    responseData.message || "User unread notification count fetched successfully",
            };
        } else {
            throw new Error(responseData.message || "Failed to fetch notification count");
        }
    } catch (error) {
        console.error("Error fetching notification count:", error);

        let errorMessage = "Failed to fetch notification count";

        if (error.response) {
            const status = error.response.status;
            const serverMessage =
                error.response.data?.message || error.response.data?.error;

            if (status >= 400 && status < 500) {
                errorMessage = serverMessage || `Client error: ${status}`;
            } else if (status >= 500) {
                errorMessage = serverMessage || `Server error: ${status}`;
            }
        } else if (error.request) {
            errorMessage = "Network error: Unable to connect to server";
        } else {
            errorMessage = error.message || "Unknown error occurred";
        }

        return {
            success: false,
            count: 0,
            error: errorMessage,
        };
    }
};

/**
 * Mark a single notification as read
 * @param {string} userNotificationId - The ID of the notification to mark as read
 */
export const markAsRead = async (userNotificationId) => {
    try {
        if (!userNotificationId) {
            throw new Error("UserNotificationId is required");
        }

        const response = await axiosInstance.post(
            `${API_ENDPOINTS.NOTIFICATIONS}/MarkAsRead`,
            { UserNotificationId: userNotificationId, isRead: true },
            {
                showLoader: false,
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        const responseData = response.data;

        if (responseData.statusCode === 200 && responseData.status === "Success") {
            return {
                success: true,
                message: responseData.message || "Notification marked as read successfully",
            };
        } else {
            throw new Error(responseData.message || "Failed to mark notification as read");
        }
    } catch (error) {
        console.error("Error marking notification as read:", error);

        return {
            success: false,
            error: error.message || "Failed to mark notification as read",
        };
    }
};

/**
 * Mark all notifications as read for the current user
 * POST /api/Notification/MarkAllAsRead
 */
export const markAllNotificationsAsRead = async () => {
    try {
        const response = await axiosInstance.post(
            `${API_ENDPOINTS.NOTIFICATIONS}/MarkAllAsRead`,
            {}, // empty body — the API identifies the user via the auth token
            {
                showLoader: false,
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        const responseData = response.data;

        if (responseData.statusCode === 200 && responseData.status === "Success") {
            return {
                success: true,
                message: responseData.message || "All notifications marked as read successfully",
            };
        } else {
            throw new Error(responseData.message || "Failed to mark all notifications as read");
        }
    } catch (error) {
        console.error("Error marking all notifications as read:", error);

        return {
            success: false,
            error: error.message || "Failed to mark all notifications as read",
        };
    }
};